/**
 * The header's mega menus.
 *
 * One implementation, any number of panels. A panel declares itself with
 * `data-dn-mega` and names its trigger with `data-dn-mega-trigger` — the path
 * the navigation link points at, e.g. "/topics" or "/speakers". Everything
 * else is read from the markup, so adding a third panel needs no JS change.
 *
 * Each panel ships as plain content with every entry a real link, and each
 * trigger ships as an ordinary navigation link to a real page. With this file
 * absent both still work: the link goes somewhere and the panel is a list.
 * What the script adds is the collapsing, the caret and the live filter.
 *
 * Triggers are found by href rather than by a class the editor could remove:
 * the menu is editable in the Site Editor, and a class is one careless save
 * away from disappearing.
 */

/**
 * How much clearance to leave between the caret's point and the nav item it
 * points at. The caret is sized from the gap the header actually leaves, which
 * changes with the header's fluid padding, so this is the one fixed number.
 */
const CARET_CLEARANCE = 6;

/** The caret's half-width as a fraction of its height, measured off the comp. */
const CARET_ASPECT = 0.87;

/** Below this the caret is not worth drawing at all. */
const CARET_MIN = 16;

/**
 * Whether the visitor has asked for reduced motion.
 *
 * @return {boolean} True when animation should be skipped.
 */
function reducedMotion() {
	return window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;
}

/**
 * The navigation link a panel names as its trigger.
 *
 * Matched with and without the trailing slash, because whether one is there
 * depends on how the link was entered in the Site Editor.
 *
 * @param {string} path The path from data-dn-mega-trigger.
 * @return {?HTMLElement} The link, or null.
 */
function findTrigger( path ) {
	const clean = path.replace( /\/$/, '' );

	const candidates = Array.from(
		document.querySelectorAll(
			`.wp-block-navigation a[href$="${ clean }"], .wp-block-navigation a[href$="${ clean }/"]`
		)
	);

	// Never the copy inside the mobile overlay. That template part holds its own
	// navigation, and binding a panel to one of its links would drop a
	// desktop-positioned mega menu into the phone menu — which is the shape the
	// overlay's own collapsible navigation exists to replace.
	//
	// `.navigation-overlay` alone, NOT the responsive container around it:
	// measured, after excluding both. Core wraps the navigation's own list in
	// `.wp-block-navigation__responsive-container` at every width — the
	// container is what changes behaviour at the breakpoint, not what marks a
	// link as mobile — so excluding it left nothing to bind and killed both
	// mega menus on the desktop.
	return (
		candidates.find( ( link ) => ! link.closest( '.navigation-overlay' ) ) || null
	);
}

/**
 * Wire one panel to its trigger.
 *
 * @param {HTMLElement} panel   The mega menu.
 * @param {HTMLElement} trigger The navigation link that opens it.
 * @param {Function}    onOpen  Called with this panel when it opens, so the
 *                              others can close.
 * @return {{close: Function, panel: HTMLElement}} A handle for the caller.
 */
function initPanel( panel, trigger, onOpen ) {
	const search = panel.querySelector( '.dn-mega__search' );
	const filterable = Array.from( panel.querySelectorAll( '[data-dn-filter]' ) );
	const empty = panel.querySelector( '.dn-mega__empty' );
	const groups = Array.from( panel.querySelectorAll( '.dn-mega__group' ) );

	// The link becomes a button in everything but tag name. It keeps its href,
	// so a middle-click or a modified click still opens the page behind it —
	// the panel is an enhancement over that page, not a replacement for it.
	trigger.setAttribute( 'aria-expanded', 'false' );
	trigger.setAttribute( 'aria-controls', panel.id );
	trigger.classList.add( 'dn-mega__trigger' );

	const isOpen = () => 'true' === trigger.getAttribute( 'aria-expanded' );

	/**
	 * Point the caret at the trigger and size it to reach.
	 *
	 * Both are measured. Where the trigger sits is not knowable from CSS — the
	 * nav is centred, translatable and editable. Neither is how far the caret
	 * must travel to reach it: the header's padding is fluid, so the gap
	 * changes with the viewport.
	 */
	function place() {
		const rect = trigger.getBoundingClientRect();
		const panelRect = panel.getBoundingClientRect();

		panel.style.setProperty(
			'--dn-mega-caret',
			`${ Math.round( rect.left - panelRect.left + rect.width / 2 ) }px`
		);

		// The caret spans the gap between the bottom of the nav item and the
		// top of the panel, less a little clearance, so its point always lands
		// just under the word it belongs to however tall the header is.
		const reach = Math.round( panelRect.top - rect.bottom - CARET_CLEARANCE );

		panel.style.setProperty(
			'--dn-mega-caret-height',
			`${ Math.max( CARET_MIN, reach ) }px`
		);

		// No height is measured for the panel any more: it takes its full
		// height and the page scrolls (see .dn-mega in _mega-menu.scss).
	}

	function open() {
		onOpen( panel );

		panel.hidden = false;
		place();

		// A frame between unhiding and the class, or the transition has no
		// start state to travel from.
		requestAnimationFrame( () => panel.classList.add( 'is-open' ) );

		trigger.setAttribute( 'aria-expanded', 'true' );
	}

	function close( { focusTrigger = false } = {} ) {
		if ( ! isOpen() ) {
			return;
		}

		panel.classList.remove( 'is-open' );
		trigger.setAttribute( 'aria-expanded', 'false' );

		// Hidden only once it has finished shrinking, or it vanishes mid-travel.
		//
		// Matched on target and property, not just on the first transitionend to
		// arrive. transitionend bubbles, and the caret inside the panel animates
		// its own transform on the same gesture — an unguarded `once` listener
		// would spend itself on the caret and hide the panel while it was still
		// halfway up.
		const finish = ( event ) => {
			if ( event && ( event.target !== panel || 'grid-template-rows' !== event.propertyName ) ) {
				return;
			}

			panel.hidden = true;
			panel.removeEventListener( 'transitionend', finish );
		};

		if ( reducedMotion() ) {
			finish();
		} else {
			panel.addEventListener( 'transitionend', finish );
		}

		if ( focusTrigger ) {
			trigger.focus();
		}
	}

	trigger.addEventListener( 'click', ( event ) => {
		// A modified click is a request for the page, not the panel.
		if ( event.metaKey || event.ctrlKey || event.shiftKey || 1 === event.button ) {
			return;
		}

		event.preventDefault();

		if ( isOpen() ) {
			close();
			return;
		}

		open();
	} );

	// Filtering. Compared against data-dn-filter, which the server already
	// lower-cased, so a keystroke does no case folding across sixty entries.
	if ( search && filterable.length ) {
		search.addEventListener( 'input', () => {
			const term = search.value.trim().toLowerCase();
			let shown = 0;

			filterable.forEach( ( el ) => {
				const match = ! term || el.getAttribute( 'data-dn-filter' ).includes( term );
				const row = el.closest( '.dn-mega__filter-item' ) || el;

				row.hidden = ! match;

				if ( match ) {
					shown++;
				}
			} );

			// A group whose every entry is filtered out keeps its heading
			// otherwise, which reads as a category that has gone empty rather
			// than one that does not match. Tested on the rows, not on the
			// entries — the entry is never the element that gets hidden.
			groups.forEach( ( group ) => {
				group.hidden = ! Array.from(
					group.querySelectorAll( '.dn-mega__filter-item' )
				).some( ( row ) => ! row.hidden );
			} );

			if ( empty ) {
				empty.hidden = 0 !== shown;
				empty.textContent =
					0 === shown
						? empty.getAttribute( 'data-dn-empty-template' ).replace(
								'%s',
								search.value.trim()
						  )
						: '';
			}
		} );
	}

	window.addEventListener( 'resize', () => {
		if ( isOpen() ) {
			place();
		}
	} );

	// No scroll listener. It kept the panel's measured height fresh, and the
	// panel no longer has one; the caret's reach cannot change on scroll,
	// because the header is in normal flow and the nav and the panel move
	// together.

	return { panel, trigger, close, isOpen };
}

function init() {
	const panels = Array.from( document.querySelectorAll( '[data-dn-mega]' ) );

	if ( ! panels.length ) {
		return;
	}

	const menus = [];

	// Only one panel open at a time: two gradient sheets stacked on top of one
	// another is not a state the design has an answer for.
	const closeOthers = ( keep ) =>
		menus.forEach( ( menu ) => {
			if ( menu.panel !== keep ) {
				menu.close();
			}
		} );

	panels.forEach( ( panel ) => {
		const trigger = findTrigger( panel.getAttribute( 'data-dn-mega-trigger' ) || '' );

		if ( ! trigger || ! panel.id ) {
			return;
		}

		menus.push( initPanel( panel, trigger, closeOthers ) );
	} );

	if ( ! menus.length ) {
		return;
	}

	const openMenu = () => menus.find( ( menu ) => menu.isOpen() );

	document.addEventListener( 'keydown', ( event ) => {
		if ( 'Escape' !== event.key ) {
			return;
		}

		const menu = openMenu();

		if ( menu ) {
			menu.close( { focusTrigger: true } );
		}
	} );

	document.addEventListener( 'click', ( event ) => {
		const menu = openMenu();

		if ( ! menu ) {
			return;
		}

		if ( menu.panel.contains( event.target ) || menu.trigger.contains( event.target ) ) {
			return;
		}

		menu.close();
	} );

	// Focus leaving the panel closes it, which is what makes it usable by
	// keyboard without trapping focus inside: tabbing past the last entry
	// carries on down the page rather than looping.
	document.addEventListener( 'focusin', ( event ) => {
		const menu = openMenu();

		if ( ! menu ) {
			return;
		}

		if ( ! menu.panel.contains( event.target ) && ! menu.trigger.contains( event.target ) ) {
			menu.close();
		}
	} );
}

if ( 'loading' === document.readyState ) {
	document.addEventListener( 'DOMContentLoaded', init );
} else {
	init();
}
